import React, { useEffect, useMemo, useState } from "react";
import { RotateCcw, Play, Square } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Label } from "../../components/ui/Label";
import { Textarea } from "../../components/ui/Textarea";
import { Skeleton } from "../../components/ui/Skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/AlertDialog";
import { GenerationProgress } from "../../components/admin/GenerationProgress";
import { useStreamRunner } from "../../hooks/useAdminStream";
import {
  usePrompt,
  useResetPrompt,
  useSavePrompt,
} from "../../hooks/useAdminPrompt";
import { diffLines } from "../../lib/lineDiff";

export const PromptTab: React.FC = () => {
  const { data, isLoading } = usePrompt();
  const save = useSavePrompt();
  const reset = useResetPrompt();
  const stream = useStreamRunner();

  const [draft, setDraft] = useState("");
  const [tag, setTag] = useState("");

  useEffect(() => {
    if (data) setDraft(data.prompt);
  }, [data]);

  const isDirty = Boolean(data) && draft !== data?.prompt;
  const diff = useMemo(
    () => (data ? diffLines(draft, data.default) : { added: [], removed: [] }),
    [draft, data]
  );
  const differsFromDefault = diff.added.length > 0 || diff.removed.length > 0;

  const handlePreview = () => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    stream.start(`?tag=${encodeURIComponent(trimmed)}&preview=1`, {
      prompt: draft,
    });
  };

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        <div>
          <h2 className="font-medium text-foreground mb-1">Prompt editorial</h2>
          <p className="text-sm text-muted-foreground">
            Instruções de estilo que a IA recebe ao resumir os commits. O
            contrato de saída em JSON é gerado automaticamente a partir do
            schema e não precisa estar no prompt.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="system-prompt">Instruções</Label>
          <Textarea
            id="system-prompt"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="font-mono text-[0.8125rem] leading-relaxed min-h-[24rem]"
            spellCheck={false}
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <p className="text-xs text-muted-foreground mr-auto inline-flex items-center gap-2">
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${
                isDirty ? "bg-primary" : "bg-muted-foreground/50"
              }`}
              aria-hidden="true"
            />
            {isDirty
              ? "Alterações não salvas"
              : data.isCustom
                ? "Prompt personalizado salvo"
                : "Usando o prompt padrão"}
          </p>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="secondary" disabled={!data.isCustom}>
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                Restaurar padrão
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Restaurar o prompt padrão?</AlertDialogTitle>
                <AlertDialogDescription>
                  O prompt personalizado será descartado e as próximas gerações
                  voltam a usar o texto que vem no código.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => reset.mutate()}>
                  Restaurar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            onClick={() => save.mutate(draft)}
            disabled={!isDirty || draft.trim().length === 0 || save.isLoading}
          >
            {save.isLoading ? "Salvando…" : "Salvar prompt"}
          </Button>
        </div>
      </div>

      {differsFromDefault && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="font-medium text-foreground">
            Diferenças em relação ao padrão
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
                Adicionadas ({diff.added.length})
              </p>
              <pre className="text-xs font-mono whitespace-pre-wrap break-words text-success">
                {diff.added.join("\n") || "nenhuma"}
              </pre>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
                Removidas ({diff.removed.length})
              </p>
              <pre className="text-xs font-mono whitespace-pre-wrap break-words text-destructive">
                {diff.removed.join("\n") || "nenhuma"}
              </pre>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        <div>
          <h2 className="font-medium text-foreground mb-1">Preview</h2>
          <p className="text-sm text-muted-foreground">
            Gera o changelog de uma tag com o texto acima, mesmo sem salvar.
            Nada é gravado no banco.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="preview-tag">Tag</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              id="preview-tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="v1.2.0"
              className="font-mono sm:flex-1"
            />
            {stream.isRunning ? (
              <Button variant="secondary" onClick={stream.abort}>
                <Square className="w-3.5 h-3.5" aria-hidden="true" />
                Cancelar
              </Button>
            ) : (
              <Button onClick={handlePreview} disabled={!tag.trim()}>
                <Play className="w-3.5 h-3.5" aria-hidden="true" />
                Gerar preview
              </Button>
            )}
          </div>
        </div>

        <GenerationProgress
          isRunning={stream.isRunning}
          tag={stream.tag}
          phase={stream.phase}
          provider={stream.provider}
          envelope={stream.envelope}
          markdown={stream.markdown}
          usage={stream.usage}
          error={stream.error}
          errorDetail={stream.errorDetail}
          showContent
        />
      </div>
    </div>
  );
};
